import {
  DownloadOutlined,
  FileOutlined,
  FolderAddOutlined
} from "@ant-design/icons"
import { Button, Image, Space, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import type { MessageAttachment } from "../types/messaging.types.ts"

const formatSize = (bytes: number | null): string => {
  if (bytes === null) return ""
  const units = ["B", "KB", "MB", "GB"]
  let size = bytes
  let unit = 0
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024
    unit += 1
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

interface AttachmentCardProps {
  attachment: MessageAttachment
  canSaveToProjectFiles: boolean
  onSaveToProjectFiles?: (attachment: MessageAttachment) => void
}

export function AttachmentCard({
  attachment,
  canSaveToProjectFiles,
  onSaveToProjectFiles
}: AttachmentCardProps) {
  const { t } = useTranslation()

  // The bytes are served only through the authorized route, so the preview and
  // the download are the same permission check — an <img> src cannot be the
  // weaker of the two.
  const source = attachment.download_url.replace("?download=true", "")

  return (
    <div
      className={
        "mt-2 rounded-lg border border-slate-200 bg-white p-2 max-w-sm"
      }
    >
      {attachment.is_image && (
        <Image
          src={source}
          alt={attachment.file_name}
          className={"rounded-md"}
          style={{ maxHeight: 220, objectFit: "cover" }}
        />
      )}

      <Space align={"center"} className={"w-full mt-1"} size={"small"}>
        <FileOutlined className={"text-slate-500"} />
        <div className={"min-w-0 flex-1"}>
          <Typography.Text ellipsis className={"block text-sm"}>
            {attachment.file_name}
          </Typography.Text>
          <Typography.Text type={"secondary"} className={"text-xs"}>
            {[attachment.content_type, formatSize(attachment.file_size)]
              .filter(Boolean)
              .join(" · ")}
          </Typography.Text>
        </div>

        <Tooltip title={t("messages.attachment.download", "Download")}>
          <Button
            size={"small"}
            type={"text"}
            icon={<DownloadOutlined />}
            href={attachment.download_url}
            aria-label={t("messages.attachment.download", "Download")}
          />
        </Tooltip>

        {canSaveToProjectFiles && (
          <Tooltip
            title={
              attachment.saved_to_project_files
                ? t("messages.attachment.alreadySaved", "Already in Project Files")
                : t("messages.attachment.saveToFiles", "Save to Project Files")
            }
          >
            <Button
              size={"small"}
              type={"text"}
              disabled={attachment.saved_to_project_files}
              icon={<FolderAddOutlined />}
              onClick={() => onSaveToProjectFiles?.(attachment)}
              aria-label={t(
                "messages.attachment.saveToFiles",
                "Save to Project Files"
              )}
            />
          </Tooltip>
        )}
      </Space>
    </div>
  )
}
